import { verificationDefine } from "@excellent/ui/api/contracts/verification-define";
import { verificationEvidence } from "@excellent/ui/api/contracts/verification-evidence";
import { environments } from "@excellent/ui/api/contracts/environments";
import { verificationOnboarding } from "@excellent/ui/api/contracts/verification-onboarding";
import { verificationOperate } from "@excellent/ui/api/contracts/verification-operate";
import { verificationProve } from "@excellent/ui/api/contracts/verification-prove";
import { verificationShell } from "@excellent/ui/api/contracts/verification-shell";
import { universalVerification } from "@excellent/ui/api/contracts/universal-verification";
import { ApiError, apiCall, apiKey, type Args, type Operation } from "@excellent/ui/api";
import useSWR, { type SWRConfiguration, type SWRResponse } from "swr";
import type { VerificationClientKey } from "../navigation/canonical";
import type { VerificationEnvironment } from "../env";

type CacheScope = readonly ("tenant" | "environment" | "identity" | "role" | "filters" | "id")[];

export interface CanonicalClient {
  operation: unknown | readonly unknown[];
  permissionAwareCacheKey: CacheScope;
}

export const verificationCanonicalClients = {
  overview: {
    operation: verificationShell.overview,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  inbox: {
    operation: verificationShell.queue,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  workLedger: {
    operation: universalVerification.overview,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  workDetail: {
    operation: universalVerification.work,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  findingsList: {
    operation: verificationShell.findingsList,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  findingsGet: {
    operation: verificationShell.findingsGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  findingsDecisions: {
    operation: verificationShell.findingsDecisions,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  findingsReview: {
    operation: verificationShell.findingsReview,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  runsList: {
    operation: verificationShell.runsList,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  runsGet: {
    operation: verificationShell.runsGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  runsCreate: {
    operation: verificationShell.runsCreate,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  workflowsList: {
    operation: verificationDefine.workflowsList,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  workflowsGet: {
    operation: verificationDefine.workflowsGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  workflowsIntake: {
    operation: verificationDefine.workflowsIntake,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  workflowsAdmit: {
    operation: verificationDefine.workflowsAdmit,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  controlsList: {
    operation: verificationDefine.controlsList,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  controlsGet: {
    operation: verificationDefine.controlsGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  policiesList: {
    operation: verificationDefine.policiesList,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  policiesGet: {
    operation: verificationDefine.policiesGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  policyVersions: {
    operation: verificationDefine.policyVersions,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  policiesActivate: {
    operation: verificationDefine.policiesActivate,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  artifactsList: {
    operation: verificationEvidence.artifactsList,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  artifactsGet: {
    operation: verificationEvidence.artifactsGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  artifactsQueue: {
    operation: verificationEvidence.artifactsQueue,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  artifactsRedact: {
    operation: verificationEvidence.artifactsRedact,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  lineageGet: {
    operation: verificationEvidence.lineageGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  connectorsList: {
    operation: verificationEvidence.connectorsList,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  connectorsCatalog: {
    operation: verificationEvidence.connectorsCatalog,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  connectorsReconnect: {
    operation: verificationEvidence.connectorsReconnect,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  environmentsList: {
    operation: environments.list,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  outcomesList: {
    operation: [
      verificationShell.runsList,
      verificationProve.receiptsList,
      verificationEvidence.artifactsList,
    ],
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  autonomyList: {
    operation: verificationDefine.workflowsList,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  analyticsGet: {
    operation: [
      verificationShell.overview,
      verificationOperate.scorecardGet,
      verificationShell.findingsList,
    ],
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  receiptsList: {
    operation: verificationProve.receiptsList,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  receiptsGet: {
    operation: verificationProve.receiptsGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  receiptsReplay: {
    operation: verificationProve.receiptsReplay,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  packsList: {
    operation: verificationProve.packsList,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  packsGet: {
    operation: verificationProve.packsGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  packsCompose: {
    operation: verificationProve.packsCompose,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  packsDownload: {
    operation: verificationProve.packsDownload,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  auditRoomGet: {
    operation: verificationProve.auditRoomGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  auditRoomGrant: {
    operation: verificationProve.auditRoomGrant,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  auditRoomRevoke: {
    operation: verificationProve.auditRoomRevoke,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  workersList: {
    operation: verificationOperate.workersList,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  scorecardGet: {
    operation: verificationOperate.scorecardGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  runtimeGet: {
    operation: verificationOperate.runtimeGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  catalogGet: {
    operation: verificationOperate.catalogGet,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  tokensCreate: {
    operation: verificationOperate.tokensCreate,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  tokensRevoke: {
    operation: verificationOperate.tokensRevoke,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  tokensRotate: {
    operation: verificationOperate.tokensRotate,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "id"],
  },
  localEnvironmentsSpawn: {
    operation: verificationOperate.localEnvironmentsSpawn,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  localEnvironmentsReset: {
    operation: verificationOperate.localEnvironmentsReset,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  demoLab: {
    operation: [
      verificationShell.overview,
      verificationDefine.controlsList,
      verificationDefine.policiesList,
      verificationDefine.workflowsList,
      verificationEvidence.artifactsList,
      verificationEvidence.connectorsList,
      verificationShell.runsList,
      verificationProve.receiptsList,
      verificationProve.packsList,
      verificationProve.auditRoomGet,
      verificationOperate.workersList,
      verificationOperate.scorecardGet,
      verificationOperate.runtimeGet,
      verificationOperate.catalogGet,
    ],
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role", "filters"],
  },
  onboardingGet: {
    operation: verificationOnboarding.get,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  onboardingFirstLoop: {
    operation: verificationOnboarding.firstLoop,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
  onboardingLocalModel: {
    operation: verificationOnboarding.localModel,
    permissionAwareCacheKey: ["tenant", "environment", "identity", "role"],
  },
} satisfies Record<VerificationClientKey, CanonicalClient>;

const pendingMutations = new Map<string, Promise<unknown>>();
let requestGeneration = 0;
let requestEnvironment: VerificationEnvironment | null = null;

export class EnvUnresolvedError extends ApiError {
  constructor() {
    super(400, "verification-environment-unresolved");
    this.name = "EnvUnresolvedError";
  }
}

function envFromArgs(args: Args<unknown, unknown, unknown>): VerificationEnvironment | null {
  const query = args.query;
  if (!query || typeof query !== "object" || !("env" in query)) return null;
  const env = (query as { env?: unknown }).env;
  return env === "demo" || env === "real" ? env : null;
}

function requireResolvedVerificationEnvironment(
  args: Args<unknown, unknown, unknown>,
): VerificationEnvironment {
  const environment = envFromArgs(args);
  if (!environment) {
    throw new EnvUnresolvedError();
  }
  return environment;
}

export function advanceVerificationRequestGeneration(
  environment: VerificationEnvironment | null,
): number {
  requestEnvironment = environment;
  requestGeneration += 1;
  return requestGeneration;
}

function beginVerificationRequest(environment: VerificationEnvironment | null): number {
  if (environment && requestEnvironment === null) requestEnvironment = environment;
  return requestGeneration;
}

function assertFreshVerificationRequest(
  environment: VerificationEnvironment | null,
  generation: number,
): void {
  if (!environment) return;
  if (generation !== requestGeneration || requestEnvironment !== environment) {
    throw new ApiError(
      409,
      `stale verification ${environment} response discarded after environment switch`,
    );
  }
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.entries(value)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, item]) => `${JSON.stringify(key)}:${stableJson(item)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

export function verificationMutationKey<TParams, TQuery, TBody, TResult>(
  op: Operation<TParams, TQuery, TBody, TResult>,
  args: Args<TParams, TQuery, TBody>,
): string {
  requireResolvedVerificationEnvironment(args as Args<unknown, unknown, unknown>);
  return `${op.method} ${apiKey(op, args)} ${stableJson(args.body ?? null)}`;
}

export function verificationSingleFlightMutation<TParams, TQuery, TBody, TResult>(
  op: Operation<TParams, TQuery, TBody, TResult>,
  args: Args<TParams, TQuery, TBody>,
): Promise<TResult> {
  const key = verificationMutationKey(op, args);
  const existing = pendingMutations.get(key);
  if (existing) return existing as Promise<TResult>;
  const pending = verificationApiCall(op, args).finally(() => {
    pendingMutations.delete(key);
  });
  pendingMutations.set(key, pending);
  return pending;
}

export async function verificationApiCall<TParams, TQuery, TBody, TResult>(
  op: Operation<TParams, TQuery, TBody, TResult>,
  args: Args<TParams, TQuery, TBody>,
): Promise<TResult> {
  const environment = requireResolvedVerificationEnvironment(
    args as Args<unknown, unknown, unknown>,
  );
  const generation = beginVerificationRequest(environment);
  const result = await apiCall(op, args);
  assertFreshVerificationRequest(environment, generation);
  return result;
}

export function useVerificationApi<TParams, TQuery, TBody, TResult>(
  op: Operation<TParams, TQuery, TBody, TResult>,
  args: Args<TParams, TQuery, TBody> | null,
  swr?: SWRConfiguration<TResult>,
): SWRResponse<TResult> {
  if (args !== null) {
    requireResolvedVerificationEnvironment(args as Args<unknown, unknown, unknown>);
  }
  const key = args === null ? null : apiKey(op, args);
  return useSWR<TResult>(
    key,
    () => verificationApiCall(op, args as Args<TParams, TQuery, TBody>),
    swr,
  );
}

export function resetVerificationPendingMutationsForTest(): void {
  pendingMutations.clear();
  requestGeneration = 0;
  requestEnvironment = null;
}
